import * as amplitude from "@amplitude/analytics-browser";
import { useTranslation } from "@redotech/redo-i18n";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import {
  Popover,
  PopoverAnchor,
  PopoverArrow,
  PopoverContent,
} from "@redotech/redo-web/components/ui/popover";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import * as darkModeCss from "@redotech/redo-web/theme/component-colors-dark.module.css";
import classNames from "classnames";
import { memo, useEffect, type ReactNode } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useLocalStorage } from "usehooks-ts";
import { PlatformEvents } from "../../../platform-events";
import * as css from "./returns-settings-callout.module.css";

const SEEN_STORAGE_KEY = "redo.returns-settings.callout-seen";

/** Points merchants at the consolidated Returns settings page until they open it or dismiss this. */
export const ReturnsSettingsCallout = memo(function ReturnsSettingsCallout({
  href,
  children,
}: {
  href: string;
  children: ReactNode;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [seen, setSeen] = useLocalStorage(SEEN_STORAGE_KEY, false);
  const onReturnsSettings =
    pathname === href || pathname.startsWith(`${href}/`);

  useEffect(() => {
    if (onReturnsSettings && !seen) {
      setSeen(true);
    }
  }, [onReturnsSettings, seen, setSeen]);

  const dismiss = () => {
    setSeen(true);
    amplitude.track(PlatformEvents.RETURNS_SETTINGS_CALLOUT_DISMISSED);
  };

  const viewNow = () => {
    setSeen(true);
    amplitude.track(PlatformEvents.RETURNS_SETTINGS_CALLOUT_VIEW_NOW);
    navigate(href);
  };

  const open = !seen && !onReturnsSettings;
  return (
    <Popover
      modal={false}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) {
          dismiss();
        }
      }}
      open={open}
    >
      <PopoverAnchor className={open ? css.highlighted : undefined}>
        {children}
      </PopoverAnchor>
      <PopoverContent
        align="center"
        className={classNames(css.content, darkModeCss.darkMode)}
        onInteractOutside={(event) => event.preventDefault()}
        onOpenAutoFocus={(event) => event.preventDefault()}
        side="right"
        sideOffset={16}
      >
        <Flex
          bgColor="primary"
          borderRadius="lg"
          dir="column"
          gap="lg"
          p="lg"
          shadow="lg"
        >
          <Flex dir="column" gap="xs">
            <Text fontSize="xs" fontWeight="semibold" textColor="primary">
              {t("Returns settings, all in one place")}
            </Text>
            <Text fontSize="xs" fontWeight="medium" textColor="secondary">
              {t(
                "We've reorganized your Returns settings into one page. Use the tabs to quickly find and manage everything you need.",
              )}
            </Text>
          </Flex>
          <Flex gap="md" justify="flex-end">
            <RedoButton
              dataTrackId="merchant.settings.returns.callout.dismiss"
              hierarchy="secondary"
              onClick={dismiss}
              size="xs"
              text={t("Dismiss")}
            />
            <RedoButton
              dataTrackId="merchant.settings.returns.callout.view-now"
              hierarchy="primary"
              onClick={viewNow}
              size="xs"
              text={t("View now")}
            />
          </Flex>
        </Flex>
        <PopoverArrow className={css.arrow} height={8} width={16} />
      </PopoverContent>
    </Popover>
  );
});
